import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Section } from '@components/ui/Section.jsx';
import { HeroSlider } from '@components/home/HeroSlider.jsx';
import { HybridBanner } from '@components/home/HybridBanner.jsx';
import { IntroductionSection } from '@components/home/IntroductionSection.jsx';
import { ObjectivesSection } from '@components/home/ObjectivesSection.jsx';
import { WhoCanParticipate } from '@components/home/WhoCanParticipate.jsx';
import { PreviousProceedings } from '@components/home/PreviousProceedings.jsx';
import { PartnersSection } from '@components/home/PartnersSection.jsx';
import { StatsBand } from '@components/home/StatsBand.jsx';
import { BangkokShowcase } from '@components/home/BangkokShowcase.jsx';
import { Seo, buildConferenceJsonLd } from '@components/seo/Seo.jsx';
import { BANGKOK } from '@/data/siteImagery.js';
import { OptimizedImage } from '@components/ui/OptimizedImage.jsx';
import { usePreloadImages } from '@/hooks/usePreloadImages.js';
import { HERO_SLIDES, HERO_PEOPLE_PHOTOS } from '@/data/homeContent.js';
import { useConferenceDates } from '@/context/ConferenceConfigContext.jsx';
import { conferenceSeoDescription } from '@/data/conferenceDates.js';

const HOME_ABOVE_FOLD_IMAGES = [
  ...HERO_PEOPLE_PHOTOS.slice(0, 4).map((p) => p.src),
  HERO_SLIDES[1]?.image,
  '/assets/images/bangkok.webp',
].filter(Boolean);

export default function Home() {
  usePreloadImages(HOME_ABOVE_FOLD_IMAGES, { urgent: true });
  const dates = useConferenceDates();

  return (
    <>
      <Seo
        title={null}
        description={conferenceSeoDescription(dates)}
        path="/"
        type="website"
        jsonLd={buildConferenceJsonLd(dates)}
      />

      <HeroSlider />
      <HybridBanner />
      <BangkokShowcase />
      <IntroductionSection />
      <ObjectivesSection />
      <WhoCanParticipate />
      <StatsBand />
      <PreviousProceedings />
      <PartnersSection />

      <Section className="!pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-4xl p-10 sm:p-14 lg:p-20 text-white border border-ink-800 min-h-[320px] flex items-center"
        >
          <OptimizedImage
            src={BANGKOK.newBangkok.src}
            alt={BANGKOK.newBangkok.alt}
            className="absolute inset-0 h-full w-full object-cover object-right brightness-[1.1] contrast-[1.05] saturate-[1.12]"
            deferUntilVisible
          />
          <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-ink-950/85 via-ink-950/45 to-transparent" />
          <div className="absolute inset-0 bg-bangkok-sunset opacity-15 mix-blend-multiply pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-500/15 blur-3xl pointer-events-none" />
          <div className="relative max-w-3xl z-10">
            <p className="eyebrow text-accent-400 mb-4">This Legacy Builds Trust And Leads Global Research</p>
            <h2 className="h-display text-4xl sm:text-5xl lg:text-6xl text-balance">
              Join 5th ISBM Bangkok 2026
            </h2>
            <p className="mt-5 text-white/70 text-lg max-w-2xl text-pretty">
              Submit your research, register to attend, or join our global community of strategists,
              researchers, and industry leaders shaping information systems for business management.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/call-for-papers#dates" className="btn-accent">
                Important Dates <ArrowUpRight size={16} />
              </Link>
              <Link to="/call-for-papers" className="btn border border-white/30 text-white hover:bg-white/10">
                Call for Papers <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </motion.div>
      </Section>
    </>
  );
}